import { Button, Flex, Modal, Text } from '@mantine/core';
import { IconChevronRight, IconPencil } from '@tabler/icons-react';
import { UpsertEntityInput } from 'src/data-capture/merge-review/useUpsertEntities';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { getDynamicUiDataFromEntityWithFieldMetadata } from 'src/UI/dynamicUI/dynamicUiInitialValuesHelpers';
import DynamicFormNew from 'src/UI/dynamicUI/layout/DynamicFormNew';
import React from 'react';
import { useDisclosure } from '@mantine/hooks';
import { DynamicUiData, EntityWithFieldMetadata } from 'src/UI/dynamicUI/types';

type Props = {
  opened: boolean;
  onClose: () => void;
  entity: EntityWithFieldMetadata;
  onSubmit: (values: UpsertEntityInput[]) => void;
  onDelete: () => void;
  titleParts?: string[];
};

export function EntityModal({ opened, onClose, entity, onSubmit, onDelete, titleParts }: Props) {
  const initialDataInput = getDynamicUiDataFromEntityWithFieldMetadata(entity) as DynamicUiData;

  const handleSubmit = async (_clientId: string, valuesToSubmit: UpsertEntityInput[]) => {
    onSubmit(valuesToSubmit);
    onClose();
  };

  const [warningModalOpened, { open: openWarningModal, close: closeWarningModal }] =
    useDisclosure(false);

  const handleDeleteConfirm = () => {
    onDelete();
  };

  const EditButtons = () => {
    const { isEditing, setIsEditing } = useDynamicUI();

    const handleEditClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setIsEditing(true);
    };

    const handleCancelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setIsEditing(false);
    };

    const handleDeleteClick = () => {
      openWarningModal();
    };

    return (
      <Flex justify="flex-end" mt="xl">
        {isEditing ? (
          <>
            <Button variant="pill" onClick={handleCancelClick} type="button" mr="xs">
              Cancel
            </Button>
            <Button variant="pill" leftSection={<IconPencil size={14} />} type="submit">
              Save
            </Button>
          </>
        ) : (
          <>
            <Button variant="pill" onClick={handleDeleteClick} type="button" mr="xs">
              Delete
            </Button>
            <Button variant="pill" leftSection={<IconPencil size={14} />} onClick={handleEditClick}>
              Edit
            </Button>
          </>
        )}
      </Flex>
    );
  };

  let title: React.ReactNode = entity.entityType;
  if (Array.isArray(titleParts) && titleParts.length > 0) {
    title = (
      <Flex align="center" gap="xxxs">
        {titleParts
          .map((str, i) => (
            <Text key={i} fw={600} c="gray.10" size="xl">
              {str}
            </Text>
          ))
          .reduce<React.ReactNode[]>((acc, curr, i) => {
            if (i === 0) {
              return [curr];
            }

            return [
              ...acc,
              <IconChevronRight
                key={'join-' + i}
                size={16}
                fontWeight={600}
                color="var(--mantine-color-gray-10)"
              />,
              curr,
            ];
          }, [])}
      </Flex>
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} size="xl" title={title}>
      <Modal opened={warningModalOpened} onClose={closeWarningModal} title="Are you sure?" centered>
        <Text c="gray.10">Once delete you cannot recover this item.</Text>
        <Flex justify="flex-end" mt="xl">
          <Button variant="pill" onClick={handleDeleteConfirm} type="button" mr="xs">
            Yes
          </Button>
          <Button variant="pill" onClick={closeWarningModal}>
            No
          </Button>
        </Flex>
      </Modal>
      <DynamicFormNew
        uiData={initialDataInput}
        onSubmit={handleSubmit}
        footer={<EditButtons />}
        isCreateMode={false}
        numColumns={3}
        isDeletable={false}
      />
    </Modal>
  );
}
